// This code primarily handles the filtering of this data according to different criteria like substances, environmental compartments, and locations.

// At the start, it imports a JSON file called raw.json, which contains the raw environmental data.

// There are two interfaces IPnec and IMec which define the structure for PNEC and MEC environmental data

// The Filters class is a complex filter mechanism that incorporates filters for API substances, protection goals, environmental compartments, and locations. 
// The Filters object is initialized with a callback function, which is used to trigger a re-render when the selected filters change.

// The getFilteredData() method is responsible for filtering the raw data based on the currently selected filters. It does this by creating new arrays with the filtered data, which it then returns.

// There is also a series of classes related to individual filters, Substances, ProtectionGoals, EnvironmentalCompartments, Locations, and NoDetects. 
// Each of these classes inherits from the Items class, which defines common functionality for all filters. 
// It provides a way to manage a list of selectable items (with selection status), and it can retrieve all items or only those that have been selected.

// Each filter class initializes its own specific items, and potentially overrides some of the generic Items functionality to fit its specific use case.


import raw from './raw.json'

interface IDict<T> {
    [key: string]: T;
}

export interface IPnec {
    a: number // Aquatic
    m: number // Marine
    p: number // Predator
    h: number // Human
}

export interface IMec {
    c: string // Country
    s: string // Substance
    e: string // Env. Compartment
    v: number // MEC Value
    r?: IPnec // RQ bar chart values category. i.e. 0 = no detect, 5 is high risk

    rq?: number // Assigned later, rq value for particular protection goal.

    x?: number, // Northings
    y?: number, // Eastings

    p?: string, // Site

    a?: number, // authors
    t?: number, // title
    n?: number // citation
}

export class Filters {
    substances: Substances
    goals: ProtectionGoals
    compartments: EnvironmentalCompartments
    locations: Locations
    detects: NoDetects
    updateCallback: Function

    constructor(updateCallback: Function) {
        this.substances = new Substances(updateCallback)
        this.goals = new ProtectionGoals(updateCallback)
        this.compartments = new EnvironmentalCompartments(updateCallback)
        this.locations = new Locations(updateCallback)
        this.detects = new NoDetects(updateCallback)
        this.updateCallback = updateCallback
    }

    selectAll() {
        this.substances.selectAll()
        this.goals.selectAll()
        this.compartments.selectAll()
        this.locations.selectAll()
        this.detects.selectAll()
        this.updateCallback()
    }

    // TODO: split this function down a bit.
    getFilteredData() {
        let pnecFiltered = null
        let pnecsFiltered: IDict<number> = {}
        let rqsFiltered = null


        const substances = this.substances.getSelected("value")
        const compartments = this.compartments.getSelected("value")
        const locations = this.locations.getSelected("value")

        function defaultShowCondition(fieldIsPresent: boolean, conditionIsMet: boolean) {
            return (!fieldIsPresent) || (fieldIsPresent && conditionIsMet)
        }
        
        const mecsFiltered = (raw.mecs.filter((o: { s: any; e: any; c: any; v: number }) =>
            substances.includes(o.s) &&
            compartments.includes(o.e) &&
            locations.includes(o.c) &&
            defaultShowCondition(!this.detects.isShow(), (o.v !== 0))
        ) as IMec[])

        //const uniqueSubstancesInMecs = [...new Set(raw.mecs.map((o: { s: any; e: any; c: any; v: number }) => o.s))];
        //console.log(uniqueSubstancesInMecs);
        
        //console.log("total_raw_mec_entries = "+raw.mecs.length);
        //console.log("total_filtered_mec_entries = "+mecsFiltered.length);
        //console.log("total_substances_entries = "+substances.length);
        //console.log("total_compartments_entries = "+compartments.length);
        //console.log("total_locations_entries = "+locations.length);

        // PROTECTION GOALS
        const goals = this.goals.getSelected("value")
        if (goals && goals.length) {

            pnecsFiltered = {}
            for (const key in raw.pnecs) {
                if (Object.prototype.hasOwnProperty.call(raw.pnecs, key)) {
                    if (substances.includes(key) || substances.length < 1) {
                        pnecsFiltered[key] = (raw.pnecs as IDict<any>)[key][goals[0]];
                    }
                }
            }

            rqsFiltered = [0, 0, 0, 0, 0, 0, 0]
            let count = 0
            const goalKey: string = goals[0]
            for (const mec of mecsFiltered) {
                // @ts-ignore
                rqsFiltered[mec.r[goalKey]]++
                count += 1
            }
            rqsFiltered = rqsFiltered.map((o: number) => 100 * (o / count))
        }

        // Add RQs to mecs using filtered pnecs
        for (const mec of mecsFiltered) {
            // this might look slow an awful. But testing showed this was
            // as about as performant as using cached values.
            // TODO: add IMec type here.s
            mec.rq = mec.v ? (mec.v / pnecsFiltered[mec.s]) : 0
        }

        // TODO: type these:
        return [
            mecsFiltered,
            pnecsFiltered === {} ? null : pnecsFiltered,
            rqsFiltered,
        ]
    }
}

class Item {
    public value: any // Probably used for id. Using Vuetify's naming convention.
    public text: string // Using Vuetify's naming convention.
    public isSelected: boolean

    constructor(value: any, text: string, isSelected: boolean = false) {
        this.value = value
        this.text = text
        this.isSelected = isSelected
    }

    public toggle() {
        this.isSelected = !this.isSelected
    }
}
export class Items {
    protected items: Item[]
    protected updateCallback: Function

    constructor(updateCallback: Function) {
        this.updateCallback = updateCallback
        this.items = []
    }

    public getAll = (mapTo?: string): Item[] => {
        // FIXME: work out how to do this!
        // @ts-ignore
        return mapTo ? this.items.map(o => o[mapTo]) : this.items
    }

    public getSelected = (mapTo?: string): any[] => {
        const full = this.items.filter(o => o.isSelected)
        // FIXME: work out how to do this!
        // @ts-ignore
        return mapTo ? full.map(o => o[mapTo]) : full
    }

    public select = (data: number[] | number) => {
        if (typeof data === 'number') {
            for (const item of this.items) {
                item.isSelected = (data === item.value)
            }
        }
        else {
            for (const item of this.items) {
                item.isSelected = data.includes(item.value)
            }
        }
        this.updateCallback()
    }

    public selectAll = () => {
        this.items.forEach((o) => {o.isSelected = true})
    }
}


class Substances extends Items {
    constructor(updateCallback: Function) {
        super(updateCallback)
        for (const substanceName of raw.substanceNames) {
            this.items.push(new Substance(substanceName, substanceName))
        }
    }
}
class Substance extends Item {
    constructor(value: any, text: string) {
        super(value, text, true)
    }
}



class ProtectionGoals extends Items {
    constructor(updateCallback: Function) {
        super(updateCallback)
        this.items.push(new ProtectionGoal("a", "Aquatic", true))
        // Only have aquatic protection goal for now - others will be added back in with new updates to the data from AZ
        // this.items.push(new ProtectionGoal("m", "Marine"))
        // this.items.push(new ProtectionGoal("p", "Predator"))
        // this.items.push(new ProtectionGoal("h", "Human"))
    }
}



// Commented  out to go back to only 1 protection goal (Aquatic)
//class ProtectionGoals extends Items {
//    constructor(updateCallback: Function) {
//        super(updateCallback)
//        this.items.push(new ProtectionGoal("a", "Aquatic", true))
//        this.items.push(new ProtectionGoal("m", "Marine"))
//        this.items.push(new ProtectionGoal("p", "Predator"))
//        this.items.push(new ProtectionGoal("h", "Human"))
//    }

//    selectAll = () => {
//        this.items.forEach((o) => {o.isSelected = false})
//        this.items[0].isSelected = true
//    }
//}

// Added in the following
class ProtectionGoal extends Item {
    constructor(value: any, text: string, isSelected: boolean = false) {
        super(value, text, isSelected)
    }
}

class EnvironmentalCompartments extends Items {
    constructor(updateCallback: Function) {
        super(updateCallback)
        for (const compartmentName of raw.compartmentNames) {
            this.items.push(new EnvironmentalCompartment(compartmentName, compartmentName, true))
        }
    }
}
class EnvironmentalCompartment extends Item {
    constructor(value: any, text: string, isSelected: boolean = false) {
        super(value, text, isSelected)
    }
}

class Locations extends Items {
    constructor(updateCallback: Function) {
        super(updateCallback)
        for (const countryName of raw.countryNames) {
            this.items.push(new Location(countryName, countryName, true))
        }
    }
}
class Location extends Item {
    constructor(value: any, text: string, isSelected: boolean = false) {
        super(value, text, isSelected)
    }
}

// TODO: code smell.
export class NoDetects extends Items {
    constructor(updateCallback: Function) {
        super(updateCallback)
        this.items.push(new NoDetect(0, "Detects Only", false))
        this.items.push(new NoDetect(1, "All Data", true))
    }

    isShow() {
        return this.getSelected("value")[0] ? true : false
    }

    toggle = () => {
        for (let item of this.items) {
            item.toggle()
        }
        this.updateCallback()
    }

    selectAll = () => {
        this.items[0].isSelected = false
        this.items[1].isSelected = true
    }
}
class NoDetect extends Item {
    constructor(value: number, text: string, isSelected: boolean) {
        super(value, text, isSelected)
    }
}
